import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { activateWallet } from "../../../services/UserService";
import { updateUserGroup } from "../../../services/PartnerService";
import {
  isExpenseTagPrerequisiteError,
  isStaleTeamSelectionError,
  prerequisiteMessageFor,
} from "../../../utils/prerequisiteErrors";
import PrerequisiteAlert from "../PrerequisiteAlert";
import { usePartnerHasSoloUser } from "../../../hooks/usePartnerHasSoloUser";
import TeamRevealStep, {
  initialTeamRevealState,
  isTeamRevealValid,
  resolveTeamIdFromReveal,
} from "./TeamRevealStep";

const ActivateWalletModal = ({
  show,
  onHide,
  partnerId,
  currentUser,
  allPartnerTeams,
  hasExpenseTags = true,
  expenseTagsKnown = true,
  teamsKnown = true,
  onAddExpenseTag,
  onAddTeam,
  onSuccess,
}) => {
  const hasTeams = (allPartnerTeams?.length ?? 0) > 0;
  const missingExpenseTag = expenseTagsKnown && !hasExpenseTags;
  const missingTeam = expenseTagsKnown && teamsKnown && hasExpenseTags && !hasTeams;
  const prerequisitesReady = expenseTagsKnown && teamsKnown && hasExpenseTags && hasTeams;
  const [teamId, setTeamId] = useState("");
  const [topLevelError, setTopLevelError] = useState(null);
  const [revealState, setRevealStateRaw] = useState(initialTeamRevealState);
  const [createdNewTeamId, setCreatedNewTeamId] = useState(null);
  const [createdNewTeamName, setCreatedNewTeamName] = useState(null);
  const [processing, setProcessing] = useState(false);
  const queryClient = useQueryClient();

  const setRevealState = (nextOrUpdater) => {
    setRevealStateRaw((prev) => {
      const next = typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;
      if (next.choice !== prev.choice) {
        setCreatedNewTeamId(null);
        setCreatedNewTeamName(null);
      }
      return next;
    });
  };

  const { hasSoloWalletHolder } = usePartnerHasSoloUser(partnerId);
  const defaultTeam = allPartnerTeams?.find((team) => team.is_default);
  const shouldShowRevealStep = hasSoloWalletHolder;
  const currentUserGrantsLimitEditing = currentUser?.role === "Super Admin";

  const mutation = useMutation({
    mutationFn: (resolvedTeamId) =>
      activateWallet(partnerId, currentUser?.id, { team_id: resolvedTeamId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activePartnerUsersCount", partnerId] });
      queryClient.invalidateQueries({ queryKey: ["partnerHasSoloUser", partnerId] });
      toast.success("Wallet activated successfully.");
      onSuccess?.();
      setTeamId("");
      setRevealStateRaw(initialTeamRevealState);
      setCreatedNewTeamId(null);
      setCreatedNewTeamName(null);
      setTopLevelError(null);
      onHide();
    },
    onError: (err) => {
      if (err?.response?.status === 422) {
        queryClient.invalidateQueries({ queryKey: ["expenseTags", partnerId] });
        queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
      }
      if (isExpenseTagPrerequisiteError(err)) {
        setTopLevelError(prerequisiteMessageFor(err, "Add at least one expense tag before activating a wallet."));
        return;
      }
      if (isStaleTeamSelectionError(err)) {
        setTopLevelError("The team you selected is no longer available. Please pick another.");
        setTeamId("");
        return;
      }
      toast.error(err?.response?.data?.message || "Failed to activate wallet. Please try again.");
    },
  });

  const close = () => {
    if (mutation.isPending || processing) return;
    setTeamId("");
    setRevealStateRaw(initialTeamRevealState);
    setCreatedNewTeamId(null);
    setCreatedNewTeamName(null);
    setTopLevelError(null);
    onHide();
  };

  const revealStepIsValid = !shouldShowRevealStep || isTeamRevealValid(revealState);

  const canSubmit = shouldShowRevealStep
    ? (expenseTagsKnown && teamsKnown && hasExpenseTags && revealStepIsValid)
    : (prerequisitesReady && !!teamId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mutation.isPending || processing) return;
    if (!canSubmit) return;
    setTopLevelError(null);
    setProcessing(true);
    try {
      let resolvedTeamId = teamId;
      if (shouldShowRevealStep) {
        if (revealState.choice === "new" && createdNewTeamId) {
          const trimmed = revealState.teamName.trim();
          if (trimmed && trimmed !== createdNewTeamName) {
            await updateUserGroup(partnerId, createdNewTeamId, { name: trimmed });
            queryClient.invalidateQueries({ queryKey: ["allPartnerTeams", partnerId] });
            setCreatedNewTeamName(trimmed);
          }
          resolvedTeamId = createdNewTeamId;
        } else {
          resolvedTeamId = await resolveTeamIdFromReveal({
            state: revealState,
            partnerId,
            defaultTeam,
            queryClient,
          });
          if (revealState.choice === "new" && resolvedTeamId) {
            setCreatedNewTeamId(resolvedTeamId);
            setCreatedNewTeamName(revealState.teamName.trim());
          }
        }
      }

      if (shouldShowRevealStep && !resolvedTeamId) {
        setTopLevelError("Could not prepare the team assignment. Please try again in a moment.");
        return;
      }

      mutation.mutate(resolvedTeamId);
    } catch (err) {
      const inlineMessage = err?.response?.data?.message || "Failed to prepare the team assignment. Please try again.";
      setTopLevelError(inlineMessage);
      toast.error(inlineMessage);
    } finally {
      setProcessing(false);
    }
  };

  const prerequisiteTooltip = missingExpenseTag
    ? "Add an expense tag first."
    : (missingTeam ? "Add a team first." : undefined);

  return (
    <Modal show={show} onHide={close} centered>
      <Modal.Header closeButton>
        <Modal.Title>Activate Wallet: {currentUser?.full_name}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted mb-3">
            Activating this wallet will allow <strong>{currentUser?.full_name}</strong> to initiate disbursements.
          </p>
          {missingExpenseTag && (
            <PrerequisiteAlert
              message="Add at least one expense tag before activating a wallet. Every payment spends against a tag, so setup starts there."
              actionLabel="Add expense tag"
              onAction={onAddExpenseTag}
            />
          )}
          {!shouldShowRevealStep && missingTeam && (
            <PrerequisiteAlert
              message="Add at least one team before activating a wallet. Every wallet holder belongs to a team, so create a team first."
              actionLabel="Add team"
              onAction={onAddTeam}
            />
          )}
          {topLevelError && (
            <PrerequisiteAlert message={topLevelError} />
          )}
          {shouldShowRevealStep ? (
            <TeamRevealStep
              firstName={currentUser?.first_name || currentUser?.full_name}
              defaultTeam={defaultTeam}
              roleGrantsLimitEditing={currentUserGrantsLimitEditing}
              state={revealState}
              onStateChange={setRevealState}
            />
          ) : (
            <div className="mb-3">
              <label className="form-label">Assign to User Group <span className="text-danger">*</span></label>
              <select
                className="form-control"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                required
                disabled={mutation.isPending || processing}
              >
                <option value="">Select a group</option>
                {allPartnerTeams?.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close} disabled={mutation.isPending || processing}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="success"
            disabled={mutation.isPending || processing || !canSubmit}
            title={prerequisiteTooltip}
          >
            {(mutation.isPending || processing) ? <span className="spinner-border spinner-border-sm me-2" /> : null}
            Activate Wallet
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ActivateWalletModal;
